import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { VillageSatelliteImage } from "../../../domain/contracts";
import { fetchVillageSatelliteBlobByKey } from "../../../services/publicApi";
import { villageSatelliteQueryOptions } from "../../../services/queries/publicQueryOptions";

const LEFT_YEAR_PRIORITY = [2016, 2015, 2014, 2017];

function pickDefaultLeftYear(availableYears: number[]): number | null {
  const sorted = [...availableYears].sort((a, b) => a - b);
  const maxYear = sorted.length > 0 ? sorted[sorted.length - 1] : null;

  for (const priorityYear of LEFT_YEAR_PRIORITY) {
    if (sorted.includes(priorityYear) && (maxYear == null || priorityYear < maxYear)) {
      return priorityYear;
    }
  }

  if (sorted.length === 0) {
    return null;
  }

  if (sorted.length === 1) {
    return sorted[0];
  }

  return sorted[0];
}

function pickRightYearGreaterThanLeft(availableYears: number[], leftYear: number | null): number | null {
  if (availableYears.length === 0 || leftYear == null) {
    return null;
  }

  const greaterYears = availableYears.filter((year) => year > leftYear).sort((a, b) => a - b);
  if (greaterYears.length === 0) {
    return null;
  }

  if (greaterYears.includes(2018)) {
    return 2018;
  }

  const earlierThan2018 = greaterYears.filter((year) => year < 2018).sort((a, b) => b - a);
  if (earlierThan2018.length > 0) {
    return earlierThan2018[0];
  }

  return greaterYears[0];
}

function revokeYearImageMap(map: Map<number, string>) {
  for (const objectUrl of map.values()) {
    URL.revokeObjectURL(objectUrl);
  }
}

export type VillageSatelliteComparisonModel = {
  isMetadataLoading: boolean;
  hasSatelliteData: boolean;
  hasComparisonData: boolean;
  satelliteItems: VillageSatelliteImage[];
  availableYears: number[];
  leftYear: number | null;
  rightYear: number | null;
  leftImageUrl: string | null;
  rightImageUrl: string | null;
  heroImageUrl: string | null;
  sliderPercent: number;
  isPanelLoading: boolean;
  setSliderPercent: (value: number) => void;
  selectLeftYear: (year: number) => void;
  selectRightYear: (year: number) => void;
};

export function useVillageSatelliteComparison(villageRef: string | null): VillageSatelliteComparisonModel {
  const [leftYear, setLeftYear] = useState<number | null>(null);
  const [rightYear, setRightYear] = useState<number | null>(null);
  const [heroImageUrl, setHeroImageUrl] = useState<string | null>(null);
  const [sliderPercent, setSliderPercentState] = useState(50);
  const [yearImageUrls, setYearImageUrls] = useState<Map<number, string>>(new Map());
  const [loadingYears, setLoadingYears] = useState<Set<number>>(new Set());

  const yearImageUrlsRef = useRef(yearImageUrls);
  const pendingLoadsRef = useRef<Map<number, Promise<string | null>>>(new Map());
  const isExpanded = true;

  const satelliteQuery = useQuery(villageSatelliteQueryOptions(villageRef, Boolean(villageRef), "satellite"));

  useEffect(() => {
    yearImageUrlsRef.current = yearImageUrls;
  }, [yearImageUrls]);

  useEffect(() => {
    return () => {
      revokeYearImageMap(yearImageUrlsRef.current);
    };
  }, []);

  useEffect(() => {
    setLeftYear(null);
    setRightYear(null);
    setHeroImageUrl(null);
    setSliderPercentState(50);
    setLoadingYears(new Set());
    pendingLoadsRef.current.clear();
    setYearImageUrls((current) => {
      revokeYearImageMap(current);
      return new Map();
    });
  }, [villageRef]);

  const satelliteItems = useMemo(
    () => (satelliteQuery.data?.items ?? [])
      .filter((item) => item.imageType === "satellite" && typeof item.r2Key === "string" && item.r2Key.trim().length > 0),
    [satelliteQuery.data?.items],
  );

  const satelliteByYear = useMemo(() => {
    const map = new Map<number, VillageSatelliteImage>();

    for (const item of satelliteItems) {
      const year = Number.isFinite(item.imageYear) ? Math.trunc(item.imageYear) : Number.NaN;
      if (!Number.isFinite(year) || !item.r2Key || item.imageType !== "satellite") {
        continue;
      }

      const existing = map.get(year);
      if (!existing || item.imageIndex < existing.imageIndex) {
        map.set(year, item);
      }
    }

    return map;
  }, [satelliteItems]);

  const availableYears = useMemo(
    () => Array.from(satelliteByYear.keys()).sort((a, b) => a - b),
    [satelliteByYear],
  );

  useEffect(() => {
    if (availableYears.length === 0) {
      setLeftYear(null);
      return;
    }

    setLeftYear((current) => {
      if (current != null && availableYears.includes(current)) {
        return current;
      }
      return pickDefaultLeftYear(availableYears);
    });
  }, [availableYears]);

  useEffect(() => {
    if (availableYears.length === 0) {
      setRightYear(null);
      return;
    }

    setRightYear((current) => {
      if (current != null && availableYears.includes(current) && leftYear != null && current > leftYear) {
        return current;
      }

      return pickRightYearGreaterThanLeft(availableYears, leftYear);
    });
  }, [availableYears, leftYear]);

  const ensureYearImageLoaded = useCallback(async (year: number | null): Promise<string | null> => {
    if (!Number.isFinite(year)) {
      return null;
    }

    const normalizedYear = Math.trunc(Number(year));
    const cached = yearImageUrlsRef.current.get(normalizedYear);
    if (cached) {
      return cached;
    }

    const pending = pendingLoadsRef.current.get(normalizedYear);
    if (pending) {
      return pending;
    }

    const target = satelliteByYear.get(normalizedYear);
    if (!target) {
      return null;
    }

    setLoadingYears((current) => {
      const next = new Set(current);
      next.add(normalizedYear);
      return next;
    });

    const loadPromise = (async () => {
      try {
        const blob = await fetchVillageSatelliteBlobByKey(target.r2Key, target.mimeType, false);
        const objectUrl = URL.createObjectURL(blob);

        setYearImageUrls((current) => {
          if (current.has(normalizedYear)) {
            URL.revokeObjectURL(objectUrl);
            return current;
          }

          const next = new Map(current);
          next.set(normalizedYear, objectUrl);
          return next;
        });

        return objectUrl;
      } catch {
        return null;
      } finally {
        pendingLoadsRef.current.delete(normalizedYear);
        setLoadingYears((current) => {
          if (!current.has(normalizedYear)) {
            return current;
          }
          const next = new Set(current);
          next.delete(normalizedYear);
          return next;
        });
      }
    })();

    pendingLoadsRef.current.set(normalizedYear, loadPromise);
    return loadPromise;
  }, [satelliteByYear]);

  useEffect(() => {
    void ensureYearImageLoaded(leftYear);
  }, [leftYear, ensureYearImageLoaded]);

  useEffect(() => {
    void ensureYearImageLoaded(rightYear);
  }, [rightYear, ensureYearImageLoaded]);

  const leftImageUrl = leftYear != null ? yearImageUrls.get(leftYear) ?? null : null;
  const rightImageUrl = rightYear != null ? yearImageUrls.get(rightYear) ?? null : null;

  useEffect(() => {
    if (heroImageUrl) {
      return;
    }

    if (leftImageUrl) {
      setHeroImageUrl(leftImageUrl);
      return;
    }

    if (rightImageUrl) {
      setHeroImageUrl(rightImageUrl);
    }
  }, [heroImageUrl, leftImageUrl, rightImageUrl]);

  const hasComparisonData = leftYear != null && rightYear != null && rightYear > leftYear;

  const isPanelLoading = isExpanded && (
    (leftYear != null && !leftImageUrl && loadingYears.has(leftYear))
    || (rightYear != null && !rightImageUrl && loadingYears.has(rightYear))
    || (satelliteQuery.isLoading && availableYears.length === 0)
  );

  const selectLeftYear = useCallback((year: number) => {
    const normalizedYear = Math.trunc(year);
    if (!availableYears.includes(normalizedYear)) {
      return;
    }

    const maxYear = availableYears[availableYears.length - 1];
    if (availableYears.length > 1 && normalizedYear >= maxYear) {
      return;
    }

    setLeftYear(normalizedYear);
    setRightYear((current) => {
      if (current != null && availableYears.includes(current) && current > normalizedYear) {
        return current;
      }
      return pickRightYearGreaterThanLeft(availableYears, normalizedYear);
    });

    void ensureYearImageLoaded(normalizedYear);
  }, [availableYears, ensureYearImageLoaded]);

  const selectRightYear = useCallback((year: number) => {
    const normalizedYear = Math.trunc(year);
    if (!availableYears.includes(normalizedYear) || leftYear == null || normalizedYear <= leftYear) {
      return;
    }

    setRightYear(normalizedYear);
    void ensureYearImageLoaded(normalizedYear);
  }, [availableYears, ensureYearImageLoaded, leftYear]);

  return {
    isMetadataLoading: satelliteQuery.isLoading,
    hasSatelliteData: availableYears.length > 0,
    hasComparisonData,
    satelliteItems,
    availableYears,
    leftYear,
    rightYear,
    leftImageUrl,
    rightImageUrl,
    heroImageUrl,
    sliderPercent,
    isPanelLoading,
    setSliderPercent(value: number) {
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) {
        return;
      }
      setSliderPercentState(Math.max(0, Math.min(100, Math.round(numeric))));
    },
    selectLeftYear,
    selectRightYear,
  };
}
